import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { toast } from "sonner";
import { 
  Upload, Copy, Check, ChevronRight, ChevronLeft, 
  Download, FileSpreadsheet, Loader2, ExternalLink, AlertTriangle,
  Clock, Shield, RefreshCw, Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AhrefsBatchTab = () => {
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [batchNum, setBatchNum] = useState(1);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [combineFiles, setCombineFiles] = useState([]);
  const [combining, setCombining] = useState(false);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    setLoadingJobs(true);
    try {
      const response = await fetch(`${API}/ahrefs/jobs`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
        // Si hay un job en progreso, cargarlo automáticamente
        const inProgressJob = data.jobs?.find(j => j.status === "in_progress" || j.status === "pending");
        if (inProgressJob) {
          setJob(inProgressJob);
          loadBatch(inProgressJob.job_id, (inProgressJob.current_batch || 0) + 1);
        }
      }
    } catch (error) {
      console.error("Error loading jobs:", error);
    }
    setLoadingJobs(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${API}/ahrefs/upload-urls`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setJob(data);
        toast.success(`${data.total_urls} URLs cargadas en ${data.total_batches} lotes`);
        loadBatch(data.job_id, 1);
        loadJobs();
      } else {
        toast.error(data.detail || "Error al subir archivo");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setUploading(false);
    e.target.value = '';
  };

  const loadBatch = async (jobId, num) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/ahrefs/job/${jobId}/batch/${num}`, {
        credentials: "include"
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setCurrentBatch(data);
        setBatchNum(num);
        setCopied(false);
      } else {
        toast.error(data.detail || "Error al cargar lote");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setLoading(false);
  };

  const selectJob = (selectedJob) => {
    setJob(selectedJob);
    const startBatch = (selectedJob.current_batch || 0) + 1;
    loadBatch(selectedJob.job_id, Math.min(startBatch, selectedJob.total_batches));
  };

  const copyToClipboard = async () => {
    if (!currentBatch) return;
    
    try {
      await navigator.clipboard.writeText(currentBatch.urls_text);
      setCopied(true);
      toast.success("URLs copiadas al portapapeles");
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error("Error al copiar");
    }
  };

  const markBatchComplete = async () => {
    if (!job || !currentBatch) return;
    
    try {
      const response = await fetch(`${API}/ahrefs/job/${job.job_id}/complete-batch/${batchNum}`, {
        method: "POST",
        credentials: "include"
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setJob(prev => ({ ...prev, current_batch: data.current_batch, status: data.status }));
        
        if (data.status === "completed") {
          toast.success("¡Todos los lotes completados!");
          loadJobs();
        } else {
          toast.success(`Lote ${batchNum} completado. Quedan ${data.remaining_batches} lotes.`);
          if (batchNum < job.total_batches) {
            loadBatch(job.job_id, batchNum + 1);
          }
        }
      }
    } catch (error) {
      toast.error("Error al marcar como completado");
    }
  };

  const deleteJob = async (jobId) => {
    if (!confirm("¿Eliminar este trabajo?")) return;
    
    try {
      const response = await fetch(`${API}/ahrefs/job/${jobId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Trabajo eliminado");
        if (job?.job_id === jobId) {
          setJob(null);
          setCurrentBatch(null);
        }
        loadJobs();
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleCombineUpload = (e) => {
    const files = Array.from(e.target.files || []);
    setCombineFiles(files);
  };

  const combineExcelFiles = async () => {
    if (combineFiles.length === 0) {
      toast.error("Selecciona archivos para combinar");
      return;
    }
    
    setCombining(true);
    const formData = new FormData();
    combineFiles.forEach(file => formData.append("files", file));
    
    try {
      const response = await fetch(`${API}/ahrefs/combine-results`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ahrefs_combined_${new Date().toISOString().slice(0,10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Archivos combinados descargados");
        setCombineFiles([]);
      } else {
        toast.error("Error al combinar archivos");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setCombining(false);
  };

  const sessionBatches = job ? Math.min(5, job.total_batches - (job.current_batch || 0)) : 0;
  const progressPercent = job ? ((job.current_batch || 0) / job.total_batches) * 100 : 0;

  if (loadingJobs) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ahrefs-batch-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Ahrefs Batch Processor</h2>
          <p className="text-muted-foreground">Procesa URLs en lotes de 200 para Ahrefs Batch Analysis</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadJobs}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-yellow-500/5 border-yellow-500/20">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Shield className="w-8 h-8 text-yellow-500 shrink-0" />
            <div className="text-sm space-y-2">
              <p className="font-semibold text-yellow-600 dark:text-yellow-400">Proceso Semi-Automatizado Seguro</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Tú controlas todo el proceso manualmente</li>
                <li>Máximo 5 lotes (1000 URLs) por sesión recomendado</li>
                <li>Copias las URLs y las pegas en Ahrefs tú mismo</li>
                <li>El progreso se guarda para continuar otro día</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Jobs List */}
      {jobs.length > 0 && !job && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-500" />
              Trabajos Anteriores
            </CardTitle>
            <CardDescription>Continúa un trabajo pendiente o empieza uno nuevo</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {jobs.map((j) => (
                <div 
                  key={j.job_id} 
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{j.total_urls?.toLocaleString()} URLs</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        j.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        j.status === 'in_progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {j.status === 'completed' ? 'Completado' : 
                         j.status === 'in_progress' ? 'En progreso' : 'Pendiente'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {j.current_batch || 0}/{j.total_batches} lotes • Creado: {new Date(j.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {j.status !== 'completed' && (
                      <Button size="sm" onClick={() => selectJob(j)}>
                        Continuar
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteJob(j.job_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Section */}
      {!job && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-500" />
              Paso 1: Sube tu Excel con URLs
            </CardTitle>
            <CardDescription>
              El Excel debe tener las URLs en la primera columna
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-center">
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleUpload}
                disabled={uploading}
                className="flex-1"
              />
              {uploading && <Loader2 className="w-6 h-6 animate-spin text-blue-500" />}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Job */}
      {job && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-green-500" />
                  Trabajo Activo
                </span>
                <Button variant="ghost" size="sm" onClick={() => { setJob(null); setCurrentBatch(null); }}>
                  Cambiar
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{job.total_urls?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">URLs Total</p>
                </div>
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{job.total_batches}</p>
                  <p className="text-xs text-muted-foreground">Lotes de 200</p>
                </div>
                <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{job.estimated_sessions}</p>
                  <p className="text-xs text-muted-foreground">Sesiones (~días)</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progreso total</span>
                  <span>{job.current_batch || 0} / {job.total_batches} lotes</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-500" />
                Paso 2: Procesa cada lote en Ahrefs
              </CardTitle>
              <CardDescription>
                Copia las URLs, pégalas en Ahrefs, descarga el Excel, y marca como completado
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadBatch(job.job_id, Math.max(1, batchNum - 1))}
                  disabled={batchNum <= 1 || loading}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>
                
                <span className="text-lg font-bold">
                  Lote {batchNum} de {job.total_batches}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadBatch(job.job_id, Math.min(job.total_batches, batchNum + 1))}
                  disabled={batchNum >= job.total_batches || loading}
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              ) : currentBatch ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-muted/50 border">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-muted-foreground">
                        {currentBatch.urls_count} URLs en este lote
                      </span>
                      <Button
                        size="sm"
                        onClick={copyToClipboard}
                        className={copied ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Copiado
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-1" />
                            Copiar URLs
                          </>
                        )}
                      </Button>
                    </div>
                    <textarea
                      readOnly
                      value={currentBatch.urls_text}
                      className="w-full h-48 p-3 rounded bg-background text-xs font-mono resize-none border"
                    />
                  </div>

                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <p className="font-semibold text-blue-700 dark:text-blue-400 mb-2">Instrucciones:</p>
                    <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                      <li>Haz clic en "Copiar URLs"</li>
                      <li>
                        Abre{" "}
                        <a 
                          href="https://app.ahrefs.com/v2-batch-analysis" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                        >
                          Ahrefs Batch Analysis
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </li>
                      <li>Pega las URLs en el campo de texto</li>
                      <li>Haz clic en "Start" y espera el análisis</li>
                      <li>Descarga el Excel con los resultados</li>
                      <li>Vuelve aquí y haz clic en "Marcar como Completado"</li>
                    </ol>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={markBatchComplete}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Marcar como Completado
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.open("https://app.ahrefs.com/v2-batch-analysis", "_blank")}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Abrir Ahrefs
                    </Button>
                  </div>
                </div>
              ) : null}

              {sessionBatches > 0 && (
                <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-yellow-700 dark:text-yellow-400 font-medium">Recomendación de seguridad</p>
                    <p className="text-muted-foreground">
                      Procesa máximo 5 lotes (1000 URLs) por día para evitar detección.
                      Hoy puedes procesar {sessionBatches} lotes más.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Combine Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-orange-500" />
            Paso 3: Combinar Excels de Ahrefs
          </CardTitle>
          <CardDescription>
            Sube todos los Excel descargados de Ahrefs para combinarlos en uno
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            type="file"
            accept=".xlsx,.xls"
            multiple
            onChange={handleCombineUpload}
            className="flex-1"
          />
          
          {combineFiles.length > 0 && (
            <div className="text-sm text-muted-foreground">
              {combineFiles.length} archivo(s) seleccionado(s): {combineFiles.map(f => f.name).join(", ")}
            </div>
          )}
          
          <Button
            onClick={combineExcelFiles}
            disabled={combineFiles.length === 0 || combining}
            className="w-full"
          >
            {combining ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Combinando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Combinar y Descargar
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AhrefsBatchTab;
